/**
 * Smoothing — Prompt.md §16 ("không cảnh báo dựa trên single frame") + edge case #6.
 *
 * Hai tầng, cố ý tách rời:
 *  1. `pushFrame` — median-of-N trên **landmark**, dập spike đơn lẻ của pose engine (một frame
 *     nhảy 15° rồi về chỗ cũ là chuyện thường xuyên, nhất là khi trẻ chớp mắt hoặc tay đi qua mặt).
 *  2. `smoothMetrics` — EMA trên **metric**, dập nhiễu biên độ nhỏ liên tục.
 *
 * Median trước EMA chứ không ngược lại: EMA là bộ lọc tuyến tính nên một outlier đủ lớn vẫn kéo
 * trung bình đi vài frame; median loại thẳng outlier trước khi nó kịp vào trung bình.
 */

import type { PostureConfig, TemporalConfig } from "@studystraight/shared-config";
import type {
  BodyLandmark,
  LandmarkMap,
  LandmarkName,
  Metric,
  PostureMetricKey,
  PostureMetrics,
  TrackingState,
  VisionResult,
} from "@studystraight/shared-types";
import { median } from "@studystraight/vision-core";

const LANDMARK_NAMES: readonly LandmarkName[] = [
  "nose",
  "leftEar",
  "rightEar",
  "leftShoulder",
  "rightShoulder",
  "leftHip",
  "rightHip",
];

const METRIC_KEYS: readonly PostureMetricKey[] = [
  "headAngle",
  "torsoAngle",
  "shoulderTilt",
  "distanceRatio",
];

const UNAVAILABLE: Metric = { value: Number.NaN, available: false };

export interface SmoothingOptions {
  posture: PostureConfig;
  temporal: TemporalConfig;
}

export interface SmoothedFrame {
  /** Frame đã lọc median — `null` khi frame bị bỏ vì `confidence` dưới ngưỡng (§16). */
  vision: VisionResult | null;
  tracking: TrackingState;
}

/** Trạng thái availability đang được lộ ra ngoài + lần đổi ý gần nhất còn đang chờ hysteresis. */
interface AvailabilityGate {
  exposed: boolean;
  pending: { available: boolean; since: number } | null;
}

export class PostureSmoothing {
  private readonly window: LandmarkMap[] = [];
  private lastValidAt: number | null = null;
  private readonly ema = new Map<PostureMetricKey, number>();
  private readonly gates = new Map<PostureMetricKey, AvailabilityGate>();

  constructor(private readonly options: SmoothingOptions) {}

  /**
   * Nạp một frame. Frame `confidence` thấp bị **bỏ** nhưng **không reset** trạng thái smoothing:
   * trẻ nghiêng người lấy cục tẩy trong 300ms không phải lý do để quên toàn bộ lịch sử và bắt đầu
   * lại từ đầu — làm vậy thì mỗi lần nhiễu là một lần mọi bộ đếm thời gian của §15 bị xoá.
   */
  pushFrame(vision: VisionResult): SmoothedFrame {
    if (vision.confidence < this.options.posture.minFrameConfidence) {
      return { vision: null, tracking: this.trackingAt(vision.timestamp) };
    }

    this.lastValidAt = vision.timestamp;
    this.window.push(vision.landmarks);
    while (this.window.length > Math.max(1, this.options.temporal.medianWindow)) {
      this.window.shift();
    }

    return {
      vision: { ...vision, landmarks: this.medianLandmarks() },
      tracking: "TRACKING",
    };
  }

  /**
   * Không có frame nào tới (pose engine không thấy người → provider trả `null`, nên `pushFrame`
   * cũng không được gọi). Đây mới là cách mất tracking xảy ra thường xuyên nhất, và nếu chỉ dựa
   * vào `pushFrame` thì hệ thống sẽ đứng yên ở "TRACKING" suốt lúc trẻ đã rời khỏi bàn.
   */
  trackingAt(now: number): TrackingState {
    if (this.lastValidAt === null) return "LOST";
    return now - this.lastValidAt >= this.options.temporal.trackingLostAfterMs
      ? "LOST"
      : "LOW_CONFIDENCE";
  }

  /**
   * EMA từng metric + hysteresis cho `available` (edge case #6).
   *
   * Vì sao cần hysteresis: `visibility` của hông dao động quanh đúng ngưỡng 0.5 là hiện tượng
   * bình thường khi trẻ ngồi sát bàn. Không có nó, `torsoAngle` bật/tắt vài lần mỗi giây, trọng số
   * score được tái phân bổ liên tục và điểm nhảy bậc — người dùng thấy "app bị lỗi" chứ không thấy
   * "hông đang bị che".
   */
  smoothMetrics(metrics: PostureMetrics, timestamp: number): PostureMetrics {
    const { smoothingAlpha } = this.options.temporal;
    const { availabilityHysteresisMs } = this.options.posture;
    const out = {} as Record<PostureMetricKey, Metric>;

    for (const key of METRIC_KEYS) {
      const incoming = metrics[key];
      const measured = incoming.available && Number.isFinite(incoming.value);

      if (measured) {
        const previous = this.ema.get(key);
        this.ema.set(
          key,
          previous === undefined
            ? incoming.value
            : smoothingAlpha * incoming.value + (1 - smoothingAlpha) * previous,
        );
      }

      const gate = this.gates.get(key);
      if (gate === undefined) {
        // Quan sát đầu tiên đi thẳng, không qua hysteresis: hysteresis để dập dao động, không
        // phải để làm hệ thống mù trong giây đầu của mỗi phiên.
        this.gates.set(key, { exposed: measured, pending: null });
      } else if (measured === gate.exposed) {
        gate.pending = null;
      } else if (gate.pending?.available !== measured) {
        gate.pending = { available: measured, since: timestamp };
      } else if (timestamp - gate.pending.since >= availabilityHysteresisMs) {
        gate.exposed = measured;
        gate.pending = null;
        // Mất metric thì xoá luôn EMA: nếu hông hiện lại sau 30 giây, trung bình cũ đã không còn
        // liên quan gì tới tư thế hiện tại nữa.
        if (!measured) this.ema.delete(key);
      }

      const exposed = this.gates.get(key)?.exposed ?? false;
      const value = this.ema.get(key);
      out[key] = exposed && value !== undefined ? { value, available: true } : { ...UNAVAILABLE };
    }

    return out as PostureMetrics;
  }

  reset(): void {
    this.window.length = 0;
    this.lastValidAt = null;
    this.ema.clear();
    this.gates.clear();
  }

  /**
   * Median theo **từng toạ độ, từng landmark**: điểm trả về có thể không trùng bất kỳ quan sát
   * nào — đúng như mong đợi của median filter và không gây vấn đề gì, vì mọi công thức phía sau
   * chỉ dùng khoảng cách và góc.
   *
   * Landmark chỉ xuất hiện ở một phần cửa sổ vẫn được tính (median trên số quan sát có thật) thay
   * vì bị loại: loại nó đi sẽ biến một landmark chập chờn thành một landmark mất hẳn.
   */
  private medianLandmarks(): LandmarkMap {
    const out: LandmarkMap = {};
    for (const name of LANDMARK_NAMES) {
      const observed = this.window
        .map((frame) => frame[name])
        .filter((lm): lm is BodyLandmark => lm !== undefined);
      if (observed.length === 0) continue;

      const landmark: BodyLandmark = {
        x: median(observed.map((lm) => lm.x)),
        y: median(observed.map((lm) => lm.y)),
      };

      const visibilities = observed
        .map((lm) => lm.visibility)
        .filter((v): v is number => v !== undefined);
      if (visibilities.length > 0) landmark.visibility = median(visibilities);

      const depths = observed.map((lm) => lm.z).filter((z): z is number => z !== undefined);
      if (depths.length > 0) landmark.z = median(depths);

      out[name] = landmark;
    }
    return out;
  }
}
